import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Link,
  Divider,
  Stack,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../services/httpClient';
import { resolvePostLoginPath } from '../../services/adminSettingsService';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';

// Same web GIS client as Flutter Citizen / Mitra (must match API GOOGLE_SIGNIN_CLIENT_ID).
const GOOGLE_CLIENT_ID =
  '392634152954-p3nkultgfkmdubnteun1lla8punp1l6b.apps.googleusercontent.com';
const GOOGLE_BUTTON_WIDTH_PX = 320;

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^\d{10}$/, 'Enter exactly 10 digits'),
  password: yup
    .string()
    .required('Password is required')
    .min(4, 'Password must be at least 4 characters'),
});

const normalizePhoneInput = (value) => value.replace(/\D/g, '').slice(0, 10);

const handlePhoneKeyDown = (event, currentValue) => {
  const allowedControlKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ];

  if (allowedControlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
    return;
  }

  if (!/^\d$/.test(event.key) || normalizePhoneInput(currentValue).length >= 10) {
    event.preventDefault();
  }
};

const loadGoogleScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

const Login = () => {
  const navigate = useNavigate();
  const {
    login,
    completeLogin,
    verifyAdminLoginOtp,
    resendAdminLoginOtp,
    getDefaultRoute,
  } = useAuth();
  const API_BASE_URL = getApiBaseUrl();
  const [showPassword, setShowPassword] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);
  const [googleHostEl, setGoogleHostEl] = useState(null);
  const [error, setError] = useState('');
  // Password → OTP 2FA for AA (app_admin / super_admin)
  const [aa2faStep, setAa2faStep] = useState(false);
  const [aa2faChallengeToken, setAa2faChallengeToken] = useState('');
  const [aa2faOtp, setAa2faOtp] = useState('');
  const [aa2faSentTo, setAa2faSentTo] = useState([]);
  const [aa2faLocalDevOtp, setAa2faLocalDevOtp] = useState('');

  const setGoogleButtonContainerRef = useCallback((node) => {
    setGoogleHostEl(node);
  }, []);

  const finishLogin = useCallback(
    async (token, user) => {
      completeLogin(token, user, 'admin');
      const userRole = user?.role || 'user';
      const nextPath = await resolvePostLoginPath(userRole, getDefaultRoute);
      navigate(nextPath, { replace: true });
    },
    [completeLogin, getDefaultRoute, navigate]
  );

  const processGoogleCredential = useCallback(
    async (credential) => {
      if (!credential) return;
      setGoogleLoading(true);
      setError('');
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/admin-google-login`, {
          idToken: credential,
        });

        if (response.data?.success && response.data?.token && response.data?.user) {
          await finishLogin(response.data.token, response.data.user);
          return;
        }

        setError(response.data?.message || 'Google login failed');
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || 'Google login failed'
        );
      } finally {
        setGoogleLoading(false);
      }
    },
    [API_BASE_URL, finishLogin]
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleHostEl) {
      return undefined;
    }

    let cancelled = false;

    const mountGoogleButton = async () => {
      try {
        await loadGoogleScript();
        if (cancelled || !googleHostEl) return;

        googleHostEl.innerHTML = '';

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => {
            if (resp?.credential) {
              processGoogleCredential(resp.credential);
            }
          },
        });

        window.google.accounts.id.renderButton(googleHostEl, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: GOOGLE_BUTTON_WIDTH_PX,
        });
        if (!cancelled) setGoogleButtonReady(true);
      } catch (e) {
        if (!cancelled) {
          const origin =
            typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';
          setError(
            `Failed to load Google Sign-In. In Google Cloud Console → OAuth Web client, add Authorized JavaScript origin: ${origin} (live already has services.knls.in).`
          );
        }
      }
    };

    mountGoogleButton();

    return () => {
      cancelled = true;
      if (googleHostEl) {
        googleHostEl.innerHTML = '';
      }
      setGoogleButtonReady(false);
    };
  }, [googleHostEl, processGoogleCredential]);

  const formik = useFormik({
    initialValues: {
      phone: '',
      password: '',
    },
    validationSchema: validationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        // Explicitly pass 'admin' session type for admin login
        const result = await login(values.phone, values.password, null, 'admin');
        if (result.success && result.requires_otp) {
          setAa2faStep(true);
          setAa2faChallengeToken(result.challenge_token || '');
          setAa2faSentTo(Array.isArray(result.otp_sent_to) ? result.otp_sent_to : []);
          setAa2faLocalDevOtp(result.local_dev_otp || '');
          setAa2faOtp(result.local_dev_otp || '');
          return;
        }
        if (result.success) {
          // Get user role from login response and redirect accordingly
          const userRole = result.user?.role || 'user';
          const nextPath = await resolvePostLoginPath(userRole, getDefaultRoute);
          navigate(nextPath, { replace: true });
        } else {
          setError(result.error || 'Login failed');
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  const verifyAa2faOtp = async () => {
    if (!/^\d{6}$/.test(aa2faOtp)) {
      setError('Enter the 6-digit OTP');
      return;
    }
    if (!aa2faChallengeToken) {
      setError('OTP session expired. Please sign in again.');
      setAa2faStep(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verifyAdminLoginOtp(aa2faChallengeToken, aa2faOtp);
      if (result.success) {
        const userRole = result.user?.role || 'user';
        const nextPath = await resolvePostLoginPath(userRole, getDefaultRoute);
        navigate(nextPath, { replace: true });
      } else {
        setError(result.error || 'OTP verification failed');
      }
    } catch (err) {
      setError('OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendAa2faOtp = async () => {
    if (!aa2faChallengeToken) {
      setError('OTP session expired. Please sign in again.');
      setAa2faStep(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await resendAdminLoginOtp(aa2faChallengeToken);
      if (result.success) {
        setAa2faChallengeToken(result.challenge_token || aa2faChallengeToken);
        setAa2faSentTo(Array.isArray(result.otp_sent_to) ? result.otp_sent_to : []);
        setAa2faLocalDevOtp(result.local_dev_otp || '');
        setAa2faOtp(result.local_dev_otp || '');
      } else {
        setError(result.error || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(formik.values.phone)) {
      setError('Enter valid 10-digit phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/send-otp`, {
        phone: formik.values.phone,
      });
      setOtpSent(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      setError('Enter OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
        phone: formik.values.phone,
        otp,
      });
      if (response.data?.success) {
        const userRole = response.data.user?.role || 'user';
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
        const nextPath = await resolvePostLoginPath(userRole, getDefaultRoute);
        navigate(nextPath, { replace: true });
      } else {
        setError(response.data?.message || 'OTP verification failed');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Clear any auto-filled values on component mount and prevent browser autocomplete
  useEffect(() => {
    // Force clear form fields immediately
    formik.resetForm();
    formik.setFieldValue('phone', '');
    formik.setFieldValue('password', '');
    
    // Clear browser autocomplete cache for this form
    if (typeof document !== 'undefined') {
      // Clear immediately
      const phoneInput = document.getElementById('phone');
      const passwordInput = document.getElementById('password');
      if (phoneInput) {
        phoneInput.value = '';
        phoneInput.setAttribute('autocomplete', 'off');
        phoneInput.blur();
      }
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.setAttribute('autocomplete', 'new-password');
        passwordInput.blur();
      }
      
      // Also clear after a short delay to catch any browser autofill
      setTimeout(() => {
        const phoneInputDelayed = document.getElementById('phone');
        const passwordInputDelayed = document.getElementById('password');
        if (phoneInputDelayed) {
          phoneInputDelayed.value = '';
          formik.setFieldValue('phone', '');
        }
        if (passwordInputDelayed) {
          passwordInputDelayed.value = '';
          formik.setFieldValue('password', '');
        }
      }, 200);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (aa2faStep) {
    return (
      <Box sx={{ mt: 2 }} autoComplete="off">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          Password verified. Enter the 6-digit OTP sent to the admin phones
          {aa2faSentTo.length > 0 ? `: ${aa2faSentTo.join(', ')}` : ''}.
          The same OTP is sent to all configured numbers.
        </Alert>
        {aa2faLocalDevOtp ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Local SMS unavailable — use OTP <strong>{aa2faLocalDevOtp}</strong>{' '}
            (dev only). On live, MSG91 delivers SMS.
          </Alert>
        ) : null}
        <TextField
          fullWidth
          id="aa2fa-otp"
          name="aa2fa-otp"
          label="Login OTP"
          value={aa2faOtp}
          onChange={(event) =>
            setAa2faOtp(normalizePhoneInput(event.target.value).slice(0, 6))
          }
          margin="normal"
          autoFocus
          inputProps={{
            maxLength: 6,
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="button"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={loading || aa2faOtp.length !== 6}
          onClick={verifyAa2faOtp}
        >
          {loading ? <CircularProgress size={24} /> : 'Verify OTP & Sign In'}
        </Button>
        <Button
          type="button"
          fullWidth
          variant="outlined"
          sx={{ mb: 1.5, py: 1.5 }}
          disabled={loading}
          onClick={resendAa2faOtp}
        >
          Resend OTP
        </Button>
        <Button
          type="button"
          fullWidth
          variant="text"
          disabled={loading}
          onClick={() => {
            setAa2faStep(false);
            setAa2faChallengeToken('');
            setAa2faOtp('');
            setAa2faSentTo([]);
            setAa2faLocalDevOtp('');
            setError('');
          }}
        >
          Back to password login
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      component="form" 
      onSubmit={formik.handleSubmit} 
      sx={{ mt: 2 }}
      autoComplete="off"
      noValidate
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="phone"
        name="phone"
        label="Phone Number"
        value={formik.values.phone}
        onChange={(event) => {
          formik.setFieldValue('phone', normalizePhoneInput(event.target.value));
        }}
        onKeyDown={(event) => handlePhoneKeyDown(event, formik.values.phone)}
        onBlur={formik.handleBlur}
        error={formik.touched.phone && Boolean(formik.errors.phone)}
        helperText={formik.touched.phone && formik.errors.phone}
        margin="normal"
        autoComplete="off"
        inputProps={{
          maxLength: 10,
          inputMode: 'numeric',
          pattern: '[0-9]*',
          autoComplete: 'off',
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: 'false',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon />
            </InputAdornment>
          ),
        }}
        placeholder="Enter 10-digit phone number"
      />
      {otpMode && (
        <TextField
          fullWidth
          id="otp"
          name="otp"
          label="OTP"
          value={otp}
          onChange={(event) => setOtp(normalizePhoneInput(event.target.value).slice(0, 6))}
          margin="normal"
          inputProps={{
            maxLength: 6,
            inputMode: 'numeric',
          }}
        />
      )}

      <TextField
        fullWidth
        id="password"
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        margin="normal"
        autoComplete="new-password"
        inputProps={{
          autoComplete: 'new-password',
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: 'false',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        type={otpMode ? 'button' : 'submit'}
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2, py: 1.5 }}
        disabled={loading || googleLoading || (otpMode && !otpSent)}
        onClick={otpMode ? verifyOtp : undefined}
      >
        {loading ? <CircularProgress size={24} /> : otpMode ? 'Verify OTP' : 'Sign In'}
      </Button>

      {otpMode && (
        <Button
          type="button"
          fullWidth
          variant="outlined"
          sx={{ mb: 2, py: 1.5 }}
          disabled={loading || googleLoading}
          onClick={sendOtp}
        >
          {otpSent ? 'Resend OTP' : 'Send OTP'}
        </Button>
      )}

      <Button
        type="button"
        fullWidth
        variant="text"
        disabled={googleLoading}
        onClick={() => {
          setOtpMode((v) => !v);
          setOtp('');
          setOtpSent(false);
          setError('');
        }}
      >
        {otpMode ? 'Use PIN login instead' : 'Use Phone OTP login'}
      </Button>

      <Stack spacing={2} sx={{ mt: 3, mb: 1, width: '100%', alignItems: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            or continue with Google
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Stack>
        {GOOGLE_CLIENT_ID ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
              opacity: loading ? 0.55 : 1,
              pointerEvents: loading ? 'none' : 'auto',
              py: 0.5,
            }}
          >
            {!googleButtonReady && !error && (
              <CircularProgress size={26} sx={{ color: 'primary.main' }} />
            )}
            <Box
              ref={setGoogleButtonContainerRef}
              sx={{
                width: GOOGLE_BUTTON_WIDTH_PX,
                maxWidth: '100%',
                minHeight: 44,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mx: 'auto',
                '& > div': {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                },
              }}
            />
            {googleLoading && (
              <Typography variant="caption" color="text.secondary">
                Signing in with Google…
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> in <code>.env</code> /{' '}
            <code>.env.development</code>, then restart <code>npm start</code>.
          </Typography>
        )}
      </Stack>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          By signing in, you agree to our{' '}
          <Link component={RouterLink} to="/locsy-terms-and-conditions" underline="hover">
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link component={RouterLink} to="/privacy-policy" underline="hover">
            Privacy Policy
          </Link>
        </Typography>
      </Box>

      {/* User Type Information */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          User Types:
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>App Admin:</strong> Full access to all features
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>Locsy Admin:</strong> Bills, coupons, services & ads management
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            <strong>User:</strong> Job alerts, coupons & ads viewing
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Your role is determined automatically based on your credentials.
        </Typography>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2" color="info.dark">
          <strong>Legal Disclaimer:</strong> This system is protected by cyber laws. 
          Unauthorized access is prohibited and may result in legal action.
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
